import DownloadIcon from "@mui/icons-material/Download";
import HistoryIcon from "@mui/icons-material/History";
import {
    Alert,
    Box,
    Button,
    Grid,
    Modal,
    Paper,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { GetDeliveryOrderByIdObject } from "../../../@types/DRO/DeliveryOrderResponse";
import { DeliveryOrderApi } from "../../../api/DeliveryOrder/DeliveryOrderApi";
import { MEDIA_BASE_URL } from "../../../config/apiConfig";
import { DeliveryOrderHistory } from "../OrderHistory/DeliveryOrderHistory";

const ViewDeliveryOrderDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [orderDetails, setOrderDetails] =
    useState<GetDeliveryOrderByIdObject>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [editableQuantities, setEditableQuantities] = useState<{
    [key: number]: number;
  }>({});
  const [backOrderModalOpen, setBackOrderModalOpen] = useState(false);
  const [invoiceId, setInvoiceId] = useState<string>("");
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);

  const fetchOrderDetails = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    setError(null);
    try {
      const response = await DeliveryOrderApi.api.getSingleDeliverOrderDetails(
        parseInt(id)
      );

      if (response?.data) {
        setOrderDetails(response.data);
      }
    } catch (error) {
      console.error("Error fetching order details", error);
      setError("Failed to fetch order details");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchOrderDetails();
  }, [fetchOrderDetails]);

  const handleQuantityChange = (
    itemId: number,
    value: string,
    maxQuantity: number
  ) => {
    const parsedValue = parseInt(value) || 0;
    if (parsedValue > maxQuantity) {
      setSnackbarMsg(
        `Quantity received cannot be more than ordered quantity (${maxQuantity})`
      );
      setSnackbarOpen(true);
    }
    const numValue =
      value === ""
        ? undefined
        : Math.max(0, Math.min(parsedValue, maxQuantity));
    setEditableQuantities((prev) => {
      const updated = { ...prev };
      if (numValue === undefined) {
        delete updated[itemId];
      } else {
        updated[itemId] = numValue;
      }
      return updated;
    });
  };

  const handleCompleteOrder = useCallback(async () => {
    // Validate that invoice ID is provided
    if (!invoiceId || invoiceId.trim() === "") {
      setSnackbarMsg("Please enter an Invoice ID before completing the order.");
      setSnackbarOpen(true);
      return;
    }

    // Check if any item has received less quantity than ordered
    const hasLessQuantityReceived = orderDetails?.deliveryOrderLines.some(
      (line) => {
        const quantityOrdered = line.quantityOrdered;
        const quantityReceived =
          editableQuantities[line.partId] ?? line.quantityReceived ?? 0;
        return quantityReceived < quantityOrdered;
      }
    );

    if (hasLessQuantityReceived) {
      // Open modal for back order confirmation
      setBackOrderModalOpen(true);
    } else {
      // Complete order without back order
      handleCreateDeliveryOrderReceived(false);
    }
  }, [orderDetails, editableQuantities, invoiceId]);

  const handleCreateDeliveryOrderReceived = async (
    createBackOrder: boolean
  ) => {
    if (!orderDetails?.id) return;
    try {
      const payload = {
        purchaseOrderId: orderDetails.purchaseOrderId,
        status: "COMPLETED",
        deliveryWarehouseId:
          orderDetails.deliveryWareHouseDetails.deliveryWarehouseId,
        deliveryOrderLineRequests: orderDetails.deliveryOrderLines.map(
          (line) => ({
            partId: line.partId,
            quantityReceived:
              editableQuantities[line.partId] ?? line.quantityReceived,
          })
        ),
        parentId: orderDetails.parentId,
        scheduledDate: orderDetails.scheduledDate,
        createBackOrder: createBackOrder,
        invoiceId: invoiceId.trim(),
      };
      await DeliveryOrderApi.api.createDeliveryOrderRecieved(
        payload,
        orderDetails.id
      );
      setSnackbarMsg("Order marked as completed successfully.");
      setSnackbarOpen(true);
      navigate(-1);
      fetchOrderDetails();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to complete order.";
      setSnackbarMsg(errorMessage);
      setSnackbarOpen(true);
      console.error("Failed to complete order", error);
    }
  };

  const handleBackOrderDecision = (createBackOrder: boolean) => {
    setBackOrderModalOpen(false);
    handleCreateDeliveryOrderReceived(createBackOrder);
  };

  const parsedItems =
    orderDetails?.deliveryOrderLines?.map((item) => {
      const partName = item?.partName ?? "N/A";
      const quantityOrdered = item?.quantityOrdered ?? 0;
      const price = item?.cost ?? 0;
      const total = item?.total ?? 0;
      const tax = item?.tax ?? null;
      const quantityReceived =
        editableQuantities[item.partId] !== undefined
          ? editableQuantities[item.partId].toString()
          : item.quantityReceived
          ? item.quantityReceived.toString()
          : "";

      const formattedTotal =
        "₹" + total.toLocaleString("en-IN", { minimumFractionDigits: 2 });
      const formattedPrice =
        "₹" + price.toLocaleString("en-IN", { minimumFractionDigits: 2 });

      return {
        id: item.partId,
        description: partName,
        referenceCode: item?.referenceCode ?? "N/A",
        demand: item.demand.toString(),
        quantity: quantityOrdered.toString(),
        received: quantityReceived,
        unit: "Units",
        price: formattedPrice,
        taxes: tax !== null && tax !== undefined ? `${tax} %` : "N/A",
        total: formattedTotal,
      };
    }) ?? [];

  const handleBack = () => {
    navigate(-1); // Go back to previous page
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ m: 4 }}>
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography color="error">{error}</Typography>
          <Button onClick={handleBack} sx={{ mt: 2 }}>
            Go Back
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ m: 4 }}>
      <Paper
        sx={{
          p: 4,
          borderRadius: "12px",
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
          backgroundColor: "#fff",
        }}>
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            textAlign: "left",
            fontWeight: "600",
            color: "#1E1E1E",
            mb: 4,
          }}>
          Delivery Order Details
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 4,
            backgroundColor: "#F8F9FA",
            padding: "12px 16px",
            borderRadius: "8px",
            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
          }}>
          <Button
            onClick={handleBack}
            sx={{
              textTransform: "none",
              color: "#646464",
              p: 0,
              "&:hover": { color: "#1E1E1E", backgroundColor: "transparent" },
            }}>
            Back
          </Button>
          <Typography variant="body2" color="#646464" sx={{ mx: 1 }}>
            &gt;
          </Typography>
          <Typography
            variant="body2"
            color="#1E1E1E"
            sx={{ fontWeight: "500" }}>
            Delivery Order #{orderDetails?.id ?? "-"}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}>
          <Box>
            <Typography variant="h6" fontWeight="600">
              Order #{orderDetails?.id ?? "-"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Reference: {orderDetails?.referenceCode ?? "-"}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<HistoryIcon />}
              onClick={() => setHistoryDialogOpen(true)}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                fontWeight: 500,
              }}>
              History
            </Button>
            {orderDetails?.status && (
              <Box
                sx={{
                  backgroundColor: (() => {
                    switch (orderDetails?.status.name) {
                      case "COMPLETED":
                        return "#4CD964";
                      case "CREATED":
                        return "#007AFF";
                      case "IN_TRANSIT":
                        return "#F0AD4E";
                      case "CANCELLED":
                        return "#FF3B30";
                      default:
                        return "#8E8E93";
                    }
                  })(),
                  color: "#fff",
                  px: 2,
                  py: "4px",
                  borderRadius: "9999px",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  textTransform: "uppercase",
                  display: "inline-block",
                }}>
                {orderDetails?.status.displayName}
              </Box>
            )}
          </Box>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ textAlign: "start" }}>
              Destination Location
            </Typography>
            <Box
              sx={{
                borderBottom: "1px solid #ccc",
                my: "1rem",
                textAlign: "start",
              }}>
              {orderDetails?.deliveryWareHouseDetails?.deliveryWarehouseName ??
                "-"}
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ textAlign: "start" }}>
              Purchase Order ID
            </Typography>
            <Box
              sx={{
                borderBottom: "1px solid #ccc",
                my: "1rem",
                textAlign: "start",
              }}>
              {orderDetails?.purchaseOrderId ?? "-"}
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ textAlign: "start" }}>
              Scheduled Date
            </Typography>
            <Box
              sx={{
                borderBottom: "1px solid #ccc",
                my: "1rem",
                textAlign: "start",
              }}>
              {orderDetails?.scheduledDate || "-"}
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ textAlign: "start" }}>
              Creation Date
            </Typography>
            <Box
              sx={{
                borderBottom: "1px solid #ccc",
                my: "1rem",
                textAlign: "start",
              }}>
              {orderDetails?.creationDate}
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ textAlign: "start" }}>
              Vendor
            </Typography>
            <Box
              sx={{
                borderBottom: "1px solid #ccc",
                my: "1rem",
                textAlign: "start",
              }}>
              {orderDetails?.vendorWarehouseDto?.vendorName ?? "-"}
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ textAlign: "start" }}>
              Parent Order ID
            </Typography>
            <Box
              sx={{
                borderBottom: "1px solid #ccc",
                my: "1rem",
                textAlign: "start",
              }}>
              {orderDetails?.parentId || "N/A"}
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ textAlign: "start" }}>
              Docket Number
            </Typography>
            <Box
              sx={{
                borderBottom: "1px solid #ccc",
                my: "1rem",
                textAlign: "start",
              }}>
              {orderDetails?.docketNumber || "-"}
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ textAlign: "start" }}>
              Courier Name
            </Typography>
            <Box
              sx={{
                borderBottom: "1px solid #ccc",
                my: "1rem",
                textAlign: "start",
              }}>
              {orderDetails?.courierName || "-"}
            </Box>
          </Grid>
          {orderDetails?.attachment && (
            <Grid item xs={12} sm={6}>
            <Box
              sx={{
                my: "1rem",
                textAlign: "start",
              }}>
              <Button
                  component="a"
                  variant="contained"
                  href={`${MEDIA_BASE_URL}/${orderDetails.attachment}`}
                  download
                  target="_blank"
                  rel="noreferrer"
                  startIcon={<DownloadIcon />}
                  sx={{
                    minWidth: 190,
                    height: 42,
                    justifyContent: "center",
                    px: 2,
                    textTransform: "none",
                    borderRadius: "999px",
                    background: "linear-gradient(135deg, #2563eb, #7c3aed)",
                    fontWeight: 600,
                    boxShadow: "0 6px 14px rgba(79, 70, 229, 0.22)",
                    "&:hover": {
                      background: "linear-gradient(135deg, #1d4ed8, #6d28d9)",
                      boxShadow: "0 8px 18px rgba(79, 70, 229, 0.3)",
                    },
                  }}>
                Download Attachment
              </Button>
            </Box>
          </Grid>
          )}
        </Grid>

        <Box mt={4}>
          <Typography
            variant="body1"
            gutterBottom
            sx={{ fontWeight: 600, display: "flex" }}>
            Order Items
          </Typography>
          <Paper
            elevation={1}
            sx={{
              borderRadius: "8px",
              overflow: "hidden",
              boxShadow: "0px 2px 6px rgba(0,0,0,0.05)",
            }}>
            <TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
              <Table size="small" sx={{ minWidth: 900 }}>
                <TableHead sx={{ backgroundColor: "#F1F3F5" }}>
                  <TableRow>
                    <TableCell
                      sx={{ fontWeight: 600, width: 40, textAlign: "center" }}>
                      #
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 220 }}>
                      Description
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 150 }}>
                      Reference Code
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        width: 90,
                        textAlign: "right",
                        whiteSpace: "nowrap",
                      }}>
                      Demand
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        width: 90,
                        textAlign: "right",
                        whiteSpace: "nowrap",
                      }}>
                      Quantity Ordered
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        width: 90,
                        textAlign: "right",
                        whiteSpace: "nowrap",
                      }}>
                      Quantity Received
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 600, width: 70, textAlign: "center" }}>
                      Unit
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        width: 100,
                        textAlign: "right",
                        whiteSpace: "nowrap",
                      }}>
                      Price
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        width: 70,
                        textAlign: "right",
                        whiteSpace: "nowrap",
                      }}>
                      Tax
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        width: 110,
                        textAlign: "right",
                        whiteSpace: "nowrap",
                      }}>
                      Total
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {parsedItems.map((item, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        backgroundColor: index % 2 === 0 ? "#fff" : "#F8F9FB",
                        "&:hover": { backgroundColor: "#FAFAFA" },
                      }}>
                      <TableCell sx={{ textAlign: "center", fontSize: 14 }}>
                        {index + 1}
                      </TableCell>
                      <TableCell sx={{ fontSize: 14 }}>
                        {item.description}
                      </TableCell>
                      <TableCell sx={{ fontSize: 14 }}>
                        {item.referenceCode}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "right",
                          fontSize: 14,
                          whiteSpace: "nowrap",
                        }}>
                        {item.demand}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "right",
                          fontSize: 14,
                          whiteSpace: "nowrap",
                        }}>
                        {item.quantity}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "right",
                          fontSize: 14,
                          whiteSpace: "nowrap",
                        }}>
                        {orderDetails?.status?.name === "CREATED" ? (
                          <input
                            value={item.received}
                            onChange={(e) =>
                              handleQuantityChange(
                                item.id,
                                e.target.value,
                                parseInt(item.quantity)
                              )
                            }
                            style={{
                              width: "60px",
                              padding: "4px",
                              textAlign: "right",
                              border: "1px solid #ddd",
                              borderRadius: "4px",
                              borderColor:
                                parseInt(item.received) >
                                parseInt(item.quantity)
                                  ? "red"
                                  : "#ddd",
                            }}
                            min="0"
                            max={item.quantity}
                          />
                        ) : (
                          item.received
                        )}
                      </TableCell>
                      <TableCell sx={{ textAlign: "center", fontSize: 14 }}>
                        {item.unit}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "right",
                          fontSize: 14,
                          whiteSpace: "nowrap",
                        }}>
                        {item.price}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "right",
                          fontSize: 14,
                          whiteSpace: "nowrap",
                        }}>
                        {item.taxes}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "right",
                          fontSize: 14,
                          whiteSpace: "nowrap",
                        }}>
                        {item.total}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Summary Section */}
            <Box
              sx={{
                p: 2,
                backgroundColor: "#F8F9FA",
                borderTop: "1px solid #E0E0E0",
              }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Total Items: {parsedItems.length}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: { sm: "right" } }}>
                    Total Amount: ₹
                    {parsedItems
                      .reduce((acc, item) => {
                        const amount = parseFloat(
                          item.total.replace(/[₹,]/g, "")
                        );
                        return acc + amount;
                      }, 0)
                      .toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
            {/* Invoice ID Input Section */}
            {orderDetails?.status?.name === "CREATED" && (
              <Box sx={{ mt: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Invoice ID *"
                      type="string"
                      variant="outlined"
                      size="small"
                      value={invoiceId}
                      onChange={(e) => setInvoiceId(e.target.value)}
                      required
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
            {/* Action Buttons Section - bottom right */}
            {orderDetails?.status?.name === "CREATED" && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                  mt: 3,
                }}>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{
                    padding: "8px 20px",
                    marginBottom: 2,
                    borderRadius: "32px",
                    backgroundColor: "#1E1E1E",
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: "0.875rem",
                    "&:hover": { backgroundColor: "#000000" },
                  }}
                  onClick={handleCompleteOrder}>
                  Complete Order
                </Button>
              </Box>
            )}
          </Paper>
        </Box>
      </Paper>

      {orderDetails?.id && (
        <DeliveryOrderHistory
          deliveryOrderId={orderDetails.id}
          variant="dialog"
          open={historyDialogOpen}
          onClose={() => setHistoryDialogOpen(false)}
        />
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}>
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarMsg.includes("successfully") ? "success" : "error"}
          sx={{ width: "100%" }}>
          {snackbarMsg}
        </Alert>
      </Snackbar>

      {/* Back Order Modal */}
      <Modal
        open={backOrderModalOpen}
        onClose={() => setBackOrderModalOpen(false)}
        aria-labelledby="backorder-modal-title"
        aria-describedby="backorder-modal-description">
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 420,
            bgcolor: "background.paper",
            borderRadius: "8px",
            boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.08)",
            p: 0,
            overflow: "hidden",
          }}>
          {/* Modal Header */}
          <Box
            sx={{
              background: "linear-gradient(to bottom, #1a1a1a, #2d2d2d)",
              p: 3,
              textAlign: "center",
            }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 12px",
              }}>
              <Typography
                sx={{
                  fontSize: "1.5rem",
                  color: "white",
                }}>
                ⚠️
              </Typography>
            </Box>
            <Typography
              id="backorder-modal-title"
              variant="h6"
              component="h2"
              sx={{
                color: "white",
                fontWeight: "500",
                fontSize: "1.125rem",
                letterSpacing: "-0.02em",
              }}>
              Items Shortage Detected
            </Typography>
          </Box>

          {/* Modal Body */}
          <Box sx={{ p: 4 }}>
            <Typography
              id="backorder-modal-description"
              sx={{
                fontSize: "0.875rem",
                color: "#4a4a4a",
                textAlign: "center",
                mb: 3.5,
                lineHeight: 1.5,
              }}>
              You have received less items than expected. Do you want to create
              a back order to the same vendor?
            </Typography>

            {/* Action Buttons */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
              }}>
              <Button
                onClick={() => handleBackOrderDecision(false)}
                variant="outlined"
                sx={{
                  flex: 1,
                  padding: "10px 20px",
                  borderRadius: "6px",
                  borderColor: "#d1d1d1",
                  color: "#2d2d2d",
                  textTransform: "none",
                  fontWeight: "400",
                  fontSize: "0.875rem",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: "#888",
                    backgroundColor: "#fafafa",
                  },
                }}>
                No, Complete As Is
              </Button>
              <Button
                onClick={() => handleBackOrderDecision(true)}
                variant="contained"
                sx={{
                  flex: 1,
                  padding: "10px 20px",
                  borderRadius: "6px",
                  background: "#1a1a1a",
                  color: "white",
                  textTransform: "none",
                  fontWeight: "500",
                  fontSize: "0.875rem",
                  boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.15)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    background: "#2d2d2d",
                    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
                  },
                }}>
                Yes, Create Back Order
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default ViewDeliveryOrderDetails;
